import { DOCUMENT } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Ally, AllyCategory } from '@main-views/allies/interfaces/allies';
import { AlliesService } from '@main-views/allies/services/allies.service';
import { ILibTbModalNotification } from 'tech-block-lib';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SwiperOptions } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { environment } from '@environment/environment';

declare const dataLayer: any;

@Component({
  selector: 'app-allies',
  templateUrl: './allies.component.html',
  styleUrls: ['./allies.component.scss']
})
export class AlliesComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  private numElements: number = 0;
  private fullElements: boolean = false;
  private viewElements?= [];
  private scrollCount: number = 0;
  private oldScrollCount: number = 0;
  private elemento: any;
  private heigthValidate: number;
  public selectedAlly = new BehaviorSubject<Ally>({} as Ally);
  public selectedCategory: string;
  public selectedOrigin: string = environment.allySection;
  public alliesCategories: AllyCategory[];
  public renderCategories: AllyCategory[];
  public filterCategories: AllyCategory[] = [{
    id: 0,
    allies: [],
    name: 'Todas',
    color_hex: '#A9FFFE',
    description: '',
    slider_duration: 10,
    slug: ''
  }];
  public selectedServiceIndex: number = 0;
  public slideConfig: SwiperOptions = {
    navigation: false,
    slidesPerView: 'auto',
    spaceBetween: 20,
    slideToClickedSlide: true,
  };
  public cardSliderConfig: SwiperOptions = {
    slidesPerView: 'auto',
    loopedSlides: 0,
    navigation: false,
    spaceBetween: 46,
    slidesPerColumn: 1,
    slidesPerColumnFill: 'row',
    slidesOffsetAfter: 16,
    pagination: {
      dynamicBullets: true,
      clickable: true,
    },
    breakpoints: {
      768: {
        spaceBetween: 24,
        pagination: {
          dynamicBullets: true,
          clickable: true,
        },
        slidesPerView: 'auto',
        slidesPerColumn: 1,
        slidesPerColumnFill: 'row',
      },
      1200: {
        spaceBetween: 24,
        pagination: false,
        slidesPerView: 4,
        slidesPerColumn: 4,
        slidesPerColumnFill: 'row',
      }
    }
  };
  public customModal: ILibTbModalNotification = {
    key: 'modalCaptureData',
    closeOnEscape: true,
    showClose: true,
    dismissableMask: true,
    acceptButtonVisible: false,
    rejectButtonVisible: false,
    class: 'modal-login',
  };
  @ViewChild('swiper', { static: false }) swiper?: SwiperComponent;

  constructor(
    private alliesService: AlliesService,
    private router: Router, private route: ActivatedRoute,
    @Inject(DOCUMENT) private document
  ) {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      this.heigthValidate = window.screen.availHeight / 2;
    } else {
      this.heigthValidate = 500;
    }
  }

  ngOnInit(): void {
    this.alliesService.getAllies()
      .pipe(
        finalize(() => {
          this.subscriptions.push(
            this.route.params.subscribe(params => {
              this.filterCategory(params['id']);
            }));
        })
      )
      .subscribe({
        next: (resp) => {
          this.alliesCategories = resp.result;
          this.filterCategories.push(...this.alliesCategories);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  /**
   * The function `selectService` updates the view elements, selected service index, and navigates to a
   * new route based on the selected service index.
   * @param {number} serviceIndex - The serviceIndex parameter is a number that represents the index of
   * the selected service in an array or list.
   */
  selectService(serviceIndex: number): void {
    this.viewElements = [];
    this.numElements = 0;
    this.fullElements = false;
    this.selectedServiceIndex = serviceIndex;
    this.swiper.index = serviceIndex - 1;
    this.swiper.updateSwiper({});
    let serviceSlug = this.filterCategories[serviceIndex].slug;
    //Tag 12C
    dataLayer.push({
      'event': 'ga_event',
      'category': 'aliados',
      'action': 'JPW - servicios filtrar',
      'label': this.filterCategories[serviceIndex].name
    });
    this.router.navigate(['aliados', `${serviceSlug}`]);
  }

  /**
   * The function filters a list of categories based on a given slug and updates the rendered
   * categories accordingly.
   * @param {string} slug - The `slug` parameter is a string that represents a category's unique
   * identifier. It is used to filter the `alliesCategories` array and retrieve the category with a
   * matching slug.
   */
  filterCategory(slug: string) {
    if (slug) {
      let cats = this.alliesCategories.filter((category) => category.slug === slug);
      if (cats.length === 0) {
        this.router.navigate(['404']);
      } else {
        this.renderCategories = cats;
        let index = this.alliesCategories.findIndex((el) => el.slug === this.renderCategories[0].slug);
        this.selectedServiceIndex = index + 1;
        this.swiper.index = index;
        this.swiper.updateSwiper({});
      }
    } else {
      this.renderCategories = this.alliesCategories;
    }
  }

  @HostListener('window:scroll', ['$event'])
  /**
   * If the window is scrolled more than 30 pixels from the top, add the class 'sticky' to the
   * menuCategories element
   * @param e - the event object
   */
  onWindowScroll(e) {
    let menuCategories = this.document.getElementById('menu-categories');
    let headerMovement = this.document.getElementById('menu-site').offsetTop;
    let st = window.scrollY || document.documentElement.scrollTop;
    if (st > 30 - headerMovement) menuCategories.classList.add('sticky');
    else menuCategories.classList.remove('sticky');
  }

  /**
   * The function scrolls to a specific element and updates the view if necessary.
   * @param {string} event - The event parameter is a string that represents the ID of an HTML element
   * that triggered the scroll event.
   * @param {number} index - The index parameter is a number that is used as a reference to identify a
   * specific element or section in the code. It is used to determine which element to scroll to or
   * which set of elements to update.
   */
  scroll(event: string, index: number) {
    if (this.renderCategories.length > 1) {
      this.getNumberElements(index);
      if (!this.fullElements)
        this.viewElements.push(this.document.getElementById(event));
      else {
        this.scrollCount += 1;
        if (index === 0 && this.scrollCount - this.oldScrollCount >= 30) {
          this.updateSwiperElement(event);
        }
      }
      this.detectTopPositionScroll();
    }
  }

  /**
   * This function updates the number of elements and checks if all elements are filled based on the
   * given index.
   * @param {number} index - index is a number parameter representing the index of an element.
   */
  getNumberElements(index: number) {
    this.numElements = this.numElements < index ? index : this.numElements;
    if (index < this.numElements) this.fullElements = true;
  }

  /**
   * This function detects if the user has scrolled to the top of the page and resets some variables if
   * so.
   */
  detectTopPositionScroll() {
    if ((window.scrollY || document.documentElement.scrollTop) === 0) {
      this.elemento = '';
      this.selectedServiceIndex = 0;
    }
  }

  /**
   * This function updates a Swiper element based on the current viewport and selected event.
   * @param {string} event - The `event` parameter is a string representing the name of an event that
   * triggered the `updateSwiperElement` function. It is used to determine whether or not to update the
   * `selectedServiceIndex` and `swiper` properties.
   */
  updateSwiperElement(event: string) {
    this.oldScrollCount = this.scrollCount;
    let elem: any;
    let itemIndex: number;
    for (let i = this.viewElements.length - 1; i >= 0; i--) {
      let isInView = this.isInViewport(this.viewElements[i]);
      if (isInView) {
        elem = this.viewElements[i];
        itemIndex = i;
        break;
      }
    }
    if ((window.scrollY || document.documentElement.scrollTop) > 0) {
      if (elem && this.elemento !== event) {
        this.elemento = event;
        this.selectedServiceIndex = itemIndex + 1;
        this.swiper.index = itemIndex;
        this.swiper.updateSwiper({});
      } else {
        this.elemento = '';
      }
    } else {
      this.elemento = '';
      this.selectedServiceIndex = 0;
    }
  }

  /**
   * If the top of the element is less than the height of the viewport minus the height of the element,
   * and the top of the element is less than the height of the element, then the element is in the
   * viewport
   * @param elem - The element to be validated.
   * @returns A boolean value.
   */
  isInViewport(elem) {
    let distance = elem.getBoundingClientRect();
    return (
      distance.top <
      (window.innerHeight - this.heigthValidate ||
        document.documentElement.clientHeight - this.heigthValidate) &&
      distance.top < this.heigthValidate
    );
  }
  /**
   * The openModal function is used to handle the logic for opening a modal, pushing data to the
   * dataLayer, and redirecting to a page based on user data.
   * @param {Ally} ally - The "ally" parameter is an object representing an ally. It likely contains
   * properties such as id, name, website, etc.
   * @param {string} categoryName - The `categoryName` parameter is a string that represents the
   * category name for the event being tracked. It is used in the Google Analytics event tracking code
   * to provide additional information about the event.
   */
  openModal(ally: Ally, categoryName: string) {
    this.selectedCategory = categoryName;
    setTimeout(() => {
      this.selectedAlly.next(ally);
    }, 50);
  }
}
